import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Api } from "@/services";
import { handleApiError } from "@/modules/shared/helpers/api-error.helper";
import { USER_QUERIES } from "./usersService.queries";
import type { IUser, IProfileOption, IProfileResponse, ICreateUserPayload, IUpdateUserPayload } from "@/pages/usuarios/types";

const BASE_URL = "/app/v1/users";
const BASE_URL_LEGACY = "/sellers/authenticated/users";
const BASE_URL_RESET_PASSWORD = "/public/users/reset-password";
const BASE_URL_PROFILES = `${BASE_URL}/profiles`;
export const useGetUsers = () => {
  const getUsers = async (): Promise<{
    success: boolean;
    message: string;
    data?: IUser[];
  }> => {
    try {
      const { data } = await Api.get(BASE_URL);
      return {
        success: true,
        message: "Usuários encontrados com sucesso!",
        data,
      };
    } catch (error: any) {
      return handleApiError(error, "Erro ao buscar usuários!");
    }
  };

  return useQuery({
    queryKey: [USER_QUERIES.USERS],
    queryFn: getUsers,
  });
};

export const useGetUser = (userId: number) => {
  const getUser = async (): Promise<{
    success: boolean;
    message: string;
    data?: IUser;
  }> => {
    try {
      const { data } = await Api.get(`${BASE_URL}/${userId}`);
      return {
        success: true,
        message: "Usuário encontrado com sucesso!",
        data,
      };
    } catch (error: any) {
      return handleApiError(error, "Erro ao buscar usuário!");
    }
  };

  return useQuery({
    queryKey: [USER_QUERIES.USER_DETAIL, userId],
    queryFn: getUser,
    enabled: !!userId,
  });
};

export const useGetProfiles = (enabled = true) => {
  const getProfiles = async (): Promise<{
    success: boolean;
    message: string;
    data?: IProfileOption[];
  }> => {
    try {
      const { data } = await Api.get(BASE_URL_PROFILES);
      const profiles: IProfileOption[] = data?.map((item: IProfileResponse) => ({
        label: item.label,
        value: item.profile,
        type: item.type,
      }));
      return {
        success: true,
        message: "Perfis encontrados com sucesso!",
        data: profiles,
      };
    } catch (error: any) {
      return handleApiError(error, "Erro ao buscar perfis!");
    }
  };

  return useQuery({
    queryKey: [USER_QUERIES.PROFILES],
    queryFn: getProfiles,
    enabled,
  });
};

export const useCreateUser = () => {
  const queryClient = useQueryClient();

  const createUser = async ({
    payload,
  }: {
    payload: ICreateUserPayload;
  }): Promise<{ success: boolean; message: string; data?: IUser }> => {
    try {
      const { data } = await Api.post(BASE_URL, payload);
      return {
        success: true,
        message: "Usuário criado com sucesso!",
        data,
      };
    } catch (error: any) {
      return handleApiError(error, "Erro ao criar o usuário!");
    }
  };

  return useMutation({
    mutationFn: createUser,
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({
          queryKey: [USER_QUERIES.USERS],
        });
      }
    },
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  const updateUser = async ({
    userId,
    payload,
  }: {
    userId: number;
    payload: IUpdateUserPayload;
  }): Promise<{ success: boolean; message: string; data?: IUser }> => {
    try {
      const { data } = await Api.patch(`${BASE_URL}/${userId}`, payload);
      return {
        success: true,
        message: "Usuário atualizado com sucesso!",
        data,
      };
    } catch (error: any) {
      return handleApiError(error, "Erro ao atualizar o usuário!");
    }
  };

  return useMutation({
    mutationFn: updateUser,
    onSuccess: (response, variables) => {
      if (response.success) {
        queryClient.invalidateQueries({
          queryKey: [USER_QUERIES.USERS],
        });
        queryClient.invalidateQueries({
          queryKey: [USER_QUERIES.USER_DETAIL, variables.userId],
        });
      }
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  const deleteUser = async ({
    userId,
  }: {
    userId: number;
  }): Promise<{ success: boolean; message: string }> => {
    try {
      await Api.delete(`${BASE_URL_LEGACY}/${userId}`);
      return {
        success: true,
        message: "Usuário excluído com sucesso!",
      };
    } catch (error: any) {
      return handleApiError(error, "Erro ao excluir o usuário!");
    }
  };

  return useMutation({
    mutationFn: deleteUser,
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({
          queryKey: [USER_QUERIES.USERS],
        });
      }
    },
  });
};

export const useSendResetPassword = () => {
  const sendResetPassword = async ({
    email,
  }: {
    email: string;
  }): Promise<{ success: boolean; message: string }> => {
    try {
      await Api.patch(BASE_URL_RESET_PASSWORD, { email });
      return {
        success: true,
        message: "Email de redefinição enviado com sucesso!",
      };
    } catch (error: any) {
      return handleApiError(error, "Erro ao enviar email de redefinição!");
    }
  };

  return useMutation({
    mutationFn: sendResetPassword,
  });
};
